import {
  resolveBrandLogo,
  resolvePrimaryColor,
} from '../components/PurchaseFlow/utils/branding'

const LAST_KEY = 'bt_org_branding_last'
const slugKey = (slug) => `bt_org_branding:${String(slug).toLowerCase()}`
const eventKey = (slug, shortcode) =>
  `bt_org_branding_event:${String(slug).toLowerCase()}/${String(
    shortcode
  ).toLowerCase()}`

const EVENT_PATH = /^\/e\/([^/]+)\/([^/]+)/

function readJson(key) {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeJson(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // private mode / quota — ignore
  }
}

function brandingPayload(organization) {
  if (!organization) return null

  const logoSrc = resolveBrandLogo(null, organization)
  const hasOwnColor = Boolean(
    organization.branding?.primaryColor ||
    organization.primaryColor ||
    organization.brandColor
  )

  // List endpoints often carry a name and nothing else. Branding built from that
  // is a letter disk in the default navy, which reads as some other tenant, so
  // skip it and let the neutral loader show instead.
  if (!logoSrc && !hasOwnColor) return null

  return {
    slug: organization.slug || null,
    name: organization.name || null,
    primaryColor: resolvePrimaryColor(null, organization),
    logoSrc,
  }
}

function writeEventBranding(event, payload) {
  if (!payload) return
  const slug = event?.seoUrl || event?.slug
  const shortcode = event?.shortCode
  if (slug && shortcode) writeJson(eventKey(slug, shortcode), payload)
}

/**
 * Persist org branding for branded page-transition loaders.
 */
export function cacheOrgBranding(organization) {
  const payload = brandingPayload(organization)
  if (!payload) return null

  writeJson(LAST_KEY, payload)
  if (payload.slug) writeJson(slugKey(payload.slug), payload)
  return payload
}

/**
 * Tie an event's route to its organization so `/e/:slug/:shortcode` can be
 * branded before the event request resolves.
 */
export function cacheEventBranding(event, organization) {
  const payload = cacheOrgBranding(organization)
  writeEventBranding(event, payload)
  return payload
}

/**
 * Seed branding for every event on an organization's storefront, so opening one
 * of them paints the right logo immediately instead of after the fetch.
 */
export function cacheOrgEventBranding(events, organization) {
  const payload = cacheOrgBranding(organization)
  if (!payload) return null
  ;(events || []).forEach((event) => writeEventBranding(event, payload))
  return payload
}

/**
 * Seed branding for a list of organizations, such as the Teams row on browse, so
 * opening one paints its logo immediately. Leaves the last-used entry alone:
 * none of these is the page the shopper is on.
 */
export function cacheOrgsBranding(organizations) {
  ;(organizations || []).forEach((organization) => {
    const payload = brandingPayload(organization)
    if (!payload?.slug) return

    writeJson(slugKey(payload.slug), payload)
    ;(organization?.upcomingEvents || []).forEach((event) =>
      writeEventBranding(event, payload)
    )
  })
}

/**
 * Resolve cached branding for an organization slug.
 */
export function getCachedOrgBranding(slug) {
  if (slug) {
    const keyed = readJson(slugKey(slug))
    if (keyed?.primaryColor) return keyed
  }
  return readJson(LAST_KEY)
}

/**
 * Resolve cached branding for the current location.
 */
export function getCachedBrandingForPath(pathname = '', params = {}) {
  const eventMatch = pathname.match(EVENT_PATH)
  if (eventMatch) {
    // An event route names the event, not the organization that owns it, so the
    // only brand we can trust is one already tied to this exact event. The
    // last-used org here would paint a different tenant's logo on the loader.
    return readJson(eventKey(eventMatch[1], eventMatch[2]))
  }

  // Venue routes name a venue, which can host events for several organizations.
  const slug = /^\/venue\//i.test(pathname)
    ? null
    : params.slug || orgSlugFromPathname(pathname)

  // Same reasoning as events: when the route names an organization we have never
  // cached, the last one seen is a different tenant, not a good guess.
  if (slug) return readJson(slugKey(slug))

  return readJson(LAST_KEY)
}

/**
 * Infer an organization slug from common branded storefront paths.
 */
export function orgSlugFromPathname(pathname = '') {
  if (!pathname) return null

  const packageMatch = pathname.match(/^\/([^/]+)\/(?:package|flex-pack)\//i)
  if (packageMatch && packageMatch[1].toLowerCase() !== 'venue') {
    return packageMatch[1]
  }

  const orgMatch = pathname.match(/^\/([^/]+)\/?$/)
  if (orgMatch) {
    const segment = orgMatch[1]
    // Reserved app routes are not org slugs — leave null and fall back to last cache.
    const reserved = new Set([
      'browse',
      'login',
      'search',
      'checkout',
      'settings',
      'www',
      'menu',
      'group',
      'fundraise',
      'privacy-policy',
      'terms-conditions',
      'purchase-policy',
      'cookies-policy',
      'disclaimer',
      'my-events',
      'my-transfers',
      'my-listings',
      'my-collectables',
      'my-packages',
      'guest-passes',
      'event-details',
      'tickets',
    ])
    if (!reserved.has(segment.toLowerCase())) return segment
  }

  return null
}

/**
 * True for storefront / purchase paths that should use the tenant loader when branding exists.
 */
export function isBrandedLoaderPath(pathname = '') {
  if (!pathname) return false
  if (orgSlugFromPathname(pathname)) return true
  if (/^\/e\//.test(pathname)) return true
  if (/^\/venue\/[^/]+\/(?:package|flex-pack)\//.test(pathname)) return true
  return false
}
